import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, Optional } from '@angular/core';
import { CookieService } from '@core-mkt/services/cookie/cookie.service';
import { AnalyticsConsentService } from '../analytics/analytics-consent.service';

@Injectable({
  providedIn: 'root',
})
export class MetaTrackingService {
  constructor(
    @Inject(DOCUMENT) @Optional() private readonly document: Document,
    private readonly cookieService: CookieService,
    // Optional so MetaTrackingServerService can keep calling super() with two arguments; DI always
    // supplies it in the browser app (providedIn: 'root').
    @Optional() private readonly consentService?: AnalyticsConsentService,
  ) {}

  /**
   * Retrieves the _fbp cookie value (set by Meta Pixel)
   */
  getFbp(): string | null {
    const cookie = this.cookieService.getCookie('_fbp');
    return cookie;
  }

  /**
   * Retrieves or sets the _fbc cookie based on fbclid in the URL
   */
  getFbc(): string | null {
    if (!this.document || !this.document.location || !this.document.location.search) {
      return null;
    }

    const fbclid = new URLSearchParams(this.document.location.search).get('fbclid');

    const existingFbc = this.cookieService.getCookie('_fbc');

    if (existingFbc) {
      if (fbclid && existingFbc.endsWith(fbclid)) {
        return existingFbc;
      }
      return existingFbc;
    }

    if (fbclid) {
      const timestamp = Date.now(); // milliseconds
      const fbc = `fb.1.${timestamp}.${fbclid}`;
      this.cookieService.setCookie('_fbc', fbc, 90); // 90 days
      return fbc;
    }

    return null;
  }

  /**
   * Returns a meta context object with fbp and fbc identifiers if available. Visitors who opted out
   * of sale/sharing (cookie or Global Privacy Control) get null, and no `_fbc` cookie is written.
   */
  getMetaContext(): { fbp?: string; fbc?: string } | null {
    // Fails closed: without a consent service (never the case under DI) no Meta identifiers are sent.
    if (!this.consentService || this.consentService.isOptedOut()) {
      return null;
    }

    const fbp = this.getFbp();
    const fbc = this.getFbc();

    if (!fbp && !fbc) {
      return null;
    }

    const context: { fbp?: string; fbc?: string } = {};
    if (fbp) context.fbp = fbp;
    if (fbc) context.fbc = fbc;

    return context;
  }
}
